import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  IconButton,
  Paper,
  Grid,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import Delete from '@mui/icons-material/Delete';
import Validator from 'validator';
import { TextView, Button, TextField, Alert, Chip, Text } from '@atoms';
import {
  callCreateCoreOfficeAddress,
  callUpdateCoreOfficeAddress,
  callDeleteCorePostalCode
} from '@particles';
import { toast } from 'react-toastify';
import { isFieldVisible, resolveBusinessType, OfficeFieldKey } from '../officeFieldVisibility';

interface AddBillingAccountProps {
  locationId: string;
  businessType?: string | null;
  integration?: string | null;
  open: boolean;
  selectedOfficeAddress?: CoreOfficeAddress;
  onClose: () => void;
  onSuccess: () => void;
}

const defaultBillingAccount: {
  officeId: string;
  zooplaBranchId: string;
  location: string;
  readEmail: string;
  branchEmail: string;
  robotEmail: string;
  mortgageEmail: string;
  valuationEmail: string;
  phone: string;
  supplierNumber: string;
  wildCard: boolean;
  type: CoreOfficeAddressType;
  crmExit?: boolean;
  postalCodes?: { id?: string; postalCode: string }[];
  origin: string;
  source: string;
  introducerName: string;
  introducerId: string;
  introducerBranchName: string;
  introducerBranchId: string;
  appointmentNotificationEmail: string;
  evergladesApiKey: string;
} = {
  officeId: '',
  zooplaBranchId: '',
  location: '',
  readEmail: '',
  branchEmail: '',
  robotEmail: '',
  mortgageEmail: '',
  valuationEmail: '',
  phone: '',
  supplierNumber: '',
  wildCard: false,
  crmExit: true,
  type: 'none',
  postalCodes: [],
  origin: '',
  source: '',
  introducerName: '',
  introducerId: '',
  introducerBranchName: '',
  introducerBranchId: '',
  appointmentNotificationEmail: '',
  evergladesApiKey: ''
};

const defaultErrorBillingAccount: {
  officeId: string;
  location: string;
  readEmail: string;
  branchEmail: string;
  robotEmail: string;
  postalCodes: string;
  mortgageEmail: string;
  valuationEmail: string;
  supplierNumber: string;
  wildCard: boolean;
  crmExit: boolean;
  phone: string;
  type: string;
} = {
  officeId: '',
  location: '',
  readEmail: '',
  branchEmail: '',
  robotEmail: '',
  postalCodes: '',
  mortgageEmail: '',
  valuationEmail: '',
  supplierNumber: '',
  wildCard: false,
  crmExit: true,
  phone: '',
  type: 'none'
};

const AddOffice: React.FC<AddBillingAccountProps> = ({
  locationId,
  businessType,
  integration,
  onClose,
  open,
  onSuccess,
  selectedOfficeAddress
}: AddBillingAccountProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bt = resolveBusinessType(businessType);
  const visible = (field: OfficeFieldKey) => isFieldVisible(bt, field, integration);

  const [loading, setLoading] = useState<boolean>(false);
  const [postalCode, setPostalCode] = useState<string>('');
  const [values, setValues] = React.useState(defaultBillingAccount);

  const [error, setError] = useState<{
    title: string;
    message: string;
  }>();

  const [errors, setErrors] = useState(defaultErrorBillingAccount);

  useEffect(() => {
    if (selectedOfficeAddress) {
      setValues({
        officeId: selectedOfficeAddress.officeId,
        zooplaBranchId: selectedOfficeAddress.zooplaBranchId ?? '',
        location: selectedOfficeAddress.location,
        readEmail: selectedOfficeAddress.readEmail,
        branchEmail: selectedOfficeAddress.branchEmail,
        robotEmail: selectedOfficeAddress.robotEmail,
        mortgageEmail: selectedOfficeAddress.mortgageEmail,
        valuationEmail: selectedOfficeAddress.valuationEmail,
        wildCard: selectedOfficeAddress.wildCard,
        postalCodes: selectedOfficeAddress.postalCodes,
        phone: selectedOfficeAddress.phone,
        supplierNumber: selectedOfficeAddress.supplierNumber ?? '',
        type: selectedOfficeAddress.type,
        crmExit: selectedOfficeAddress.crmExit ?? true,
        origin: selectedOfficeAddress.origin ?? '',
        source: selectedOfficeAddress.source ?? '',
        introducerName: selectedOfficeAddress.introducerName ?? '',
        introducerId: selectedOfficeAddress.introducerId ?? '',
        introducerBranchName: selectedOfficeAddress.introducerBranchName ?? '',
        introducerBranchId: selectedOfficeAddress.introducerBranchId ?? '',
        appointmentNotificationEmail: selectedOfficeAddress.appointmentNotificationEmail ?? '',
        evergladesApiKey: selectedOfficeAddress.evergladesApiKey ?? ''
      });
    }
  }, [selectedOfficeAddress]);

  const handleChange =
    (prop: string) => (event: SelectChangeEvent | React.ChangeEvent<HTMLInputElement>) => {
      if (prop == 'postal_code') {
        setPostalCode(event.target.value);
        return;
      }

      if (prop == 'wildCard') {
        setValues({ ...values, [prop]: (event.target as HTMLInputElement).checked });
        return;
      }

      if (prop == 'crmExit') {
        setValues({ ...values, [prop]: (event.target as HTMLInputElement).checked });
        return;
      }

      setValues({ ...values, [prop]: event.target.value });
    };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateFields()) {
      toast.error('Please fill all required fields');
      return;
    }

    // Only submit fields that are visible for this businessType.
    // Hidden fields keep whatever value they had on the server (we don't blank them).
    const officeAddress: CoreOfficeAddress = {
      locationId,
      officeId: values.officeId,
      location: values.location,
      readEmail: values.readEmail,
      branchEmail: values.branchEmail,
      robotEmail: values.robotEmail,
      mortgageEmail: values.mortgageEmail,
      valuationEmail: visible('valuationEmail') ? values.valuationEmail : (selectedOfficeAddress?.valuationEmail ?? ''),
      postalCodes: values.postalCodes,
      wildCard: values.wildCard,
      phone: values.phone,
      supplierNumber: values.supplierNumber,
      type: values.type,
      crmExit: values.crmExit,
      ...(visible('zooplaBranchId') ? { zooplaBranchId: values.zooplaBranchId } : {}),
      ...(visible('origin') ? { origin: values.origin } : {}),
      ...(visible('source') ? { source: values.source } : {}),
      ...(visible('introducerName') ? { introducerName: values.introducerName } : {}),
      ...(visible('introducerId') ? { introducerId: values.introducerId } : {}),
      ...(visible('introducerBranchName') ? { introducerBranchName: values.introducerBranchName } : {}),
      ...(visible('introducerBranchId') ? { introducerBranchId: values.introducerBranchId } : {}),
      ...(visible('appointmentNotificationEmail')
        ? { appointmentNotificationEmail: values.appointmentNotificationEmail }
        : {}),
      ...(visible('evergladesApiKey') ? { evergladesApiKey: values.evergladesApiKey } : {})
    };

    if (selectedOfficeAddress && selectedOfficeAddress.id) {
      setLoading(true);
      callUpdateCoreOfficeAddress({ ...officeAddress, id: selectedOfficeAddress.id })
        .then((res) => {
          if (res.code == 200) {
            setValues(defaultBillingAccount);
            onSuccess();
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore;
            setError({ title: 'Error', message: res.error?.message ?? '' });
            setTimeout(() => {
              setError(undefined);
            }, 5000);
          }
          setLoading(false);
        })
        .catch((error) => {
          alert(error);
          setLoading(false);
        });
    } else {
      setLoading(true);
      callCreateCoreOfficeAddress(officeAddress)
        .then((res) => {
          if (res.code == 201) {
            setValues(defaultBillingAccount);
            onSuccess();
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore;
            setError({ title: 'Error', message: res.error?.message ?? '' });
            setTimeout(() => {
              setError(undefined);
            }, 5000);
          }
          setLoading(false);
        })
        .catch((error) => {
          alert(error);
          setLoading(false);
        });
    }
  };

  const validateFields = () => {
    let error = false;
    setErrors(defaultErrorBillingAccount);
    if (visible('officeId') && Validator.isEmpty(values.officeId.trim())) {
      error = true;
      setErrors((state) => ({ ...state, officeId: 'OfficeID is required' }));
    }

    if (visible('location') && Validator.isEmpty(values.location.trim())) {
      error = true;
      setErrors((state) => ({ ...state, location: 'Location is required' }));
    }

    if (visible('readEmail') && Validator.isEmpty(values.readEmail.trim())) {
      error = true;
      setErrors((state) => ({ ...state, readEmail: 'Read Email is required' }));
    }

    if (visible('branchEmail') && Validator.isEmpty(values.branchEmail.trim())) {
      error = true;
      setErrors((state) => ({ ...state, branchEmail: 'Branch Email is required' }));
    }

    if (visible('robotEmail') && Validator.isEmpty(values.robotEmail.trim())) {
      error = true;
      setErrors((state) => ({ ...state, robotEmail: 'Robot Email is required' }));
    }

    if (visible('mortgageEmail') && Validator.isEmpty(values.mortgageEmail.trim())) {
      error = true;
      setErrors((state) => ({ ...state, mortgageEmail: 'Mortgage Email is required' }));
    }

    // if (values.postalCodes?.length == 0) {
    //   error = true;
    //   setErrors((state) => ({ ...state, postal_code: 'PostalCodes is required' }));
    // }

    return error;
  };

  function validateOutwardAndInwardCode(postcode: string): boolean {
    // Define regex for outward code and the first digit of the inward code
    const pattern = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9]/i;

    // Test the postcode against the pattern
    return pattern.test(postcode.trim());
  }

  return (
    <Modal
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '16px' : '24px'
      }}
      open={open}
      onClose={() => {
        setValues(defaultBillingAccount);
        setErrors(defaultErrorBillingAccount);
        onClose();
      }}>
      <Grid
        container
        sx={{
          maxWidth: {
            xs: '100%',
            sm: '90%',
            md: '80%',
            lg: '70%',
            xl: '60%'
          },
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: { xs: '8px', sm: '16px' }
        }}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
          <Box
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              mb: 2
            }}>
            <TextView
              $fontSize={26}
              sx={{
                fontSize: { xs: '20px', sm: '24px', md: '26px' }
              }}>
              {`${selectedOfficeAddress ? 'Edit' : 'Add'} Office Address`}
            </TextView>
            <Box sx={{ flex: 1 }} />
            <IconButton
              onClick={() => {
                onClose();
                setValues(defaultBillingAccount);
                setErrors(defaultErrorBillingAccount);
              }}>
              <Close />
            </IconButton>
          </Box>
          <form onSubmit={onSubmit}>
            <Grid spacing={{ xs: 2, sm: 3 }} container>
              {visible('officeId') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Office Id'}
                    name={'officeId'}
                    errorMessage={errors.officeId}
                    fullWidth
                    placeholder="MKT"
                    inputType="text"
                    value={values.officeId}
                    onChange={handleChange('officeId')}
                  />
                </Grid>
              )}
              {visible('zooplaBranchId') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Zoopla Branch ID'}
                    name={'zooplaBranchId'}
                    fullWidth
                    inputType="text"
                    value={values.zooplaBranchId}
                    onChange={handleChange('zooplaBranchId')}
                  />
                </Grid>
              )}
              {(visible('type') || visible('wildCard') || visible('crmExit')) && (
                <Grid item xs={12} md={6}>
                  <Stack
                    mt={{ xs: 2, sm: 1.5 }}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 2 }}
                    sx={{ width: '100%' }}>
                    {visible('type') && (
                      <Stack flex={1} sx={{ width: '100%' }}>
                        <Text mb={1} $fontSize={14}>
                          Branch Type
                        </Text>
                        <Select
                          value={values?.type ?? 'none'}
                          onChange={handleChange('type')}
                          size="small"
                          fullWidth
                          sx={{
                            backgroundColor: '#ffffff',
                            fontSize: 14,
                            height: '50px',
                            width: '100%'
                          }}
                          inputProps={{ 'aria-label': 'Without label' }}>
                          <MenuItem key="none" value="none">
                            None
                          </MenuItem>
                          <MenuItem key="lettings" value="lettings">
                            Lettings
                          </MenuItem>
                          <MenuItem key="sales" value="sales">
                            Sales
                          </MenuItem>
                          <MenuItem key="both" value="both">
                            Both (Sales & Lettings)
                          </MenuItem>
                          <MenuItem key="renewal" value="renewal">
                            Renewal
                          </MenuItem>
                          <MenuItem key="remortgage" value="remortgage">
                            Remortgage
                          </MenuItem>
                          <MenuItem key="recapture" value="recapture">
                            Recapture
                          </MenuItem>
                        </Select>
                      </Stack>
                    )}
                    {visible('wildCard') && (
                      <Stack
                        sx={{
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          mt: { xs: 1, sm: 0 }
                        }}>
                        <Text mb={1} $fontSize={14}>
                          WildCard
                        </Text>
                        <Checkbox checked={values.wildCard} onChange={handleChange('wildCard')} />
                      </Stack>
                    )}
                    {visible('crmExit') && (
                      <Stack
                        sx={{
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          mt: { xs: 1, sm: 0 }
                        }}>
                        <Text mb={1} $fontSize={14}>
                          CRM Exit
                        </Text>
                        <Checkbox checked={values.crmExit} onChange={handleChange('crmExit')} />
                      </Stack>
                    )}
                  </Stack>
                </Grid>
              )}
              {visible('location') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Office Location'}
                    name={'location'}
                    placeholder="Birmingham"
                    errorMessage={errors.location}
                    fullWidth
                    inputType="text"
                    value={values.location}
                    onChange={handleChange('location')}
                  />
                </Grid>
              )}
              {visible('readEmail') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Read Email'}
                    name={'readEmail'}
                    errorMessage={errors.readEmail}
                    fullWidth
                    inputType="email"
                    value={values.readEmail}
                    onChange={handleChange('readEmail')}
                  />
                </Grid>
              )}
              {visible('branchEmail') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Branch Email'}
                    name={'branchEmail'}
                    errorMessage={errors.branchEmail}
                    fullWidth
                    inputType="email"
                    value={values.branchEmail}
                    onChange={handleChange('branchEmail')}
                  />
                </Grid>
              )}
              {visible('robotEmail') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Robot Email'}
                    name={'robotEmail'}
                    errorMessage={errors.robotEmail}
                    fullWidth
                    inputType="email"
                    value={values.robotEmail}
                    onChange={handleChange('robotEmail')}
                  />
                </Grid>
              )}
              {visible('mortgageEmail') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Mortgage Email'}
                    name={'mortgageEmail'}
                    errorMessage={errors.mortgageEmail}
                    fullWidth
                    inputType="email"
                    value={values.mortgageEmail}
                    onChange={handleChange('mortgageEmail')}
                  />
                </Grid>
              )}
              {visible('valuationEmail') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Valuation Email'}
                    name={'valuationEmail'}
                    errorMessage={errors.valuationEmail}
                    fullWidth
                    inputType="email"
                    value={values.valuationEmail}
                    onChange={handleChange('valuationEmail')}
                  />
                </Grid>
              )}
              {visible('phone') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Phone'}
                    name={'phone'}
                    errorMessage={errors.phone}
                    fullWidth
                    inputType="tel"
                    value={values.phone}
                    onChange={handleChange('phone')}
                  />
                </Grid>
              )}
              {visible('supplierNumber') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Supplier Number'}
                    name={'supplierNumber'}
                    errorMessage={errors.supplierNumber}
                    fullWidth
                    inputType="text"
                    value={values.supplierNumber}
                    onChange={handleChange('supplierNumber')}
                  />
                </Grid>
              )}
              {visible('appointmentNotificationEmail') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Appointment Notification Email'}
                    name={'appointmentNotificationEmail'}
                    fullWidth
                    inputType="email"
                    value={values.appointmentNotificationEmail}
                    onChange={handleChange('appointmentNotificationEmail')}
                  />
                </Grid>
              )}
              {visible('origin') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Origin'}
                    name={'origin'}
                    fullWidth
                    inputType="text"
                    value={values.origin}
                    onChange={handleChange('origin')}
                  />
                </Grid>
              )}
              {visible('source') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Source'}
                    name={'source'}
                    fullWidth
                    inputType="text"
                    value={values.source}
                    onChange={handleChange('source')}
                  />
                </Grid>
              )}
              {visible('introducerName') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Introducer Name'}
                    name={'introducerName'}
                    fullWidth
                    inputType="text"
                    value={values.introducerName}
                    onChange={handleChange('introducerName')}
                  />
                </Grid>
              )}
              {visible('introducerId') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Introducer ID'}
                    name={'introducerId'}
                    fullWidth
                    inputType="text"
                    value={values.introducerId}
                    onChange={handleChange('introducerId')}
                  />
                </Grid>
              )}
              {visible('introducerBranchName') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Introducer Branch Name'}
                    name={'introducerBranchName'}
                    fullWidth
                    inputType="text"
                    value={values.introducerBranchName}
                    onChange={handleChange('introducerBranchName')}
                  />
                </Grid>
              )}
              {visible('introducerBranchId') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Introducer Branch ID'}
                    name={'introducerBranchId'}
                    fullWidth
                    inputType="text"
                    value={values.introducerBranchId}
                    onChange={handleChange('introducerBranchId')}
                  />
                </Grid>
              )}
              {visible('evergladesApiKey') && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label={'Everglades API Key'}
                    name={'evergladesApiKey'}
                    fullWidth
                    inputType="text"
                    value={values.evergladesApiKey}
                    onChange={handleChange('evergladesApiKey')}
                  />
                </Grid>
              )}
            </Grid>

            {visible('postalCodes') && (
              <>
                <TextView
                  $fontSize={20}
                  sx={{
                    mt: { xs: 2, sm: 3 }
                  }}>
                  Postal Code
                </TextView>
                <Grid item xs={12} md={6}>
                  <Stack
                    spacing={{ xs: 1, sm: 2 }}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'end' }}
                    sx={{ width: '100%' }}>
                    <TextField
                      label={''}
                      name={'postal_code'}
                      fullWidth
                      inputType="text"
                      value={postalCode}
                      errorMessage={errors.postalCodes}
                      onChange={handleChange('postal_code')}
                    />
                    <Button
                      label={'Add'}
                      size="small"
                      sx={{
                        bottom: errors.postalCodes ? 20 : 5,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: 'auto', sm: '80px' }
                      }}
                      onClick={() => {
                        if (postalCode.trim().length == 0) {
                          setErrors((state) => ({ ...state, postalCodes: 'PostalCode is required' }));
                          return;
                        }

                        if (validateOutwardAndInwardCode(postalCode)) {
                          setValues({
                            ...values,
                            postalCodes: [...(values.postalCodes || []), { postalCode }]
                          });
                          setPostalCode('');
                        } else {
                          setErrors((state) => ({ ...state, postal_code: 'PostalCode Invalid' }));
                        }
                      }}
                    />
                  </Stack>
                </Grid>
                <Box
                  sx={{
                    mt: { xs: 1, sm: 1 },
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 1.5 },
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                  {values.postalCodes?.map((item, index) => (
                    <Chip
                      key={index}
                      onDelete={async () => {
                        if (selectedOfficeAddress && selectedOfficeAddress.id && item.id) {
                          await callDeleteCorePostalCode({
                            locationId,
                            officeAddressId: selectedOfficeAddress.id,
                            postalCodeId: item.id
                          });
                        }

                        values.postalCodes?.splice(index, 1);
                        setValues({ ...values, postalCodes: [...(values.postalCodes || [])] });
                      }}
                      label={item.postalCode}
                      variant="filled"
                      deleteIcon={<Delete />}
                      $backgroundColor="#f2f2f2"
                      sx={{
                        fontSize: { xs: '12px', sm: '14px' },
                        height: { xs: '32px', sm: 'auto' }
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
            {error && <Alert severity={'error'} title={error.title} message={error.message} />}

            <Box
              sx={{
                justifyContent: { xs: 'center', sm: 'flex-start' },
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                mt: { xs: 3, sm: 4 },
                width: '100%'
              }}>
              <Button
                label={`${selectedOfficeAddress ? 'Update' : 'Add'}`}
                size="small"
                type="submit"
                loading={loading}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: '120px' }
                }}
              />
            </Box>
          </form>
        </Paper>
      </Grid>
    </Modal>
  );
};
export default AddOffice;
